<!-- eslint-disable no-prototype-builtins -->
<script setup lang="ts">
import AppState, {
  calculateMaxAmount,
  recurringAppointmentPaymentOptions,
} from '@/states/app'
import {
  AlertTriangleIcon,
  InfoCircleIcon,
  Percent02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UITooltip as GHLUITooltip,
  UIAlert,
  UIForm,
  UIFormItem,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UISelect,
  UISwitch,
  UITabPane,
  UITabs,
} from '@gohighlevel/ghl-ui'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  DepositType,
  RecurringAppointmentsPaymentType,
} from '../../models/calendar'
import { PaymentProvider } from '../../utils/constants'
import { currency } from '../../utils/currencyHelper'
import {
  checkForValidPaymentProvider,
  getTooltip,
} from '../../utils/generalFunctions'
import {
  getDefaulPaymentMethod,
  getSupportedPaymentProviders,
} from '../../utils/paymentHelper'
import HeaderTile from '../common/HeaderTile.vue'
import Tooltip from '../common/Tooltip.vue'

const { t } = useI18n()

const state = reactive({
  isLoading: false,
  shouldAcceptPayment: false,
  shouldAcceptDepositAmount: false,
  isPaymentError: false,
  formData: {
    isLivePaymentMode: false,
    stripe: {
      amount: 0,
      currency: 'USD',
      chargeDescription: '',
      depositAmount: 0,
      depositPercentage: 0,
      depositType: DepositType.AMOUNT,
      recurringAppointmentsPaymentType:
        RecurringAppointmentsPaymentType.FirstAppointmentOnly,
      isCouponEnabled: false,
    },
    enableChargeGuests: false,
  },
})

const stripeFormRef = ref()

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

watch(
  () => state.shouldAcceptPayment,
  () => {
    AppState.editCalendarValidationData.shouldAcceptPayment =
      state.shouldAcceptPayment
  }
)

watch(
  () => AppState.editCalendarValidationData.enableChargeGuests,
  () => {
    state.formData.enableChargeGuests =
      AppState.editCalendarValidationData.enableChargeGuests
  }
)

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const paymentCurrencyOptions = computed(() => {
  const currencyOptions: string[] = []
  for (const key of Object.keys(currency)) {
    currencyOptions.push(currency[key].code)
  }
  return currencyOptions.map(x => {
    return {
      value: x,
      label: x,
    }
  })
})

const isRecurringPaymentEnabled = computed(() => {
  return AppState.editCalendarValidationData.enableRecurring
})

const isPaypalWithRazorpay = computed(() => {
  return (
    AppState.defaultPaymentProvider === 'custom-provider' &&
    AppState.isPayPalEnabled
  )
})

const isPartialPaymentEnabled = computed(() => {
  return (
    state.formData.stripe.amount > 0 &&
    !AppState.editCalendarValidationData.enableRecurring
  )
})

const rules = computed(() => {
  return {
    stripeAmount: {
      trigger: ['input', 'blur'],
      validator() {
        if (!state.formData.stripe.amount) {
          state.isPaymentError = false
          return true
        }
        if (state.formData.stripe.amount.toString().match(/^\d+(\.\d{3,})$/)) {
          state.isPaymentError = true
          return new Error(t('calendar_advanced.forms_payment.stripe_error'))
        }
        state.isPaymentError = false
        return true
      },
    },
    depositAmount: {
      trigger: ['input', 'blur'],
      validator() {
        if (
          state.formData.stripe.depositAmount > state.formData.stripe.amount
        ) {
          return new Error(
            t('calendar_advanced.forms_payment.deposit_amount_error')
          )
        }
        if (
          state.formData.stripe.depositAmount
            .toString()
            .match(/^\d+(\.\d{3,})$/)
        ) {
          return new Error(t('calendar_advanced.forms_payment.stripe_error'))
        }
        return true
      },
    },
    depositPercentage: {
      trigger: ['input', 'blur'],
      validator() {
        if (state.formData.stripe.depositPercentage > 100) {
          return new Error(
            t('calendar_advanced.forms_payment.deposit_percentage_error')
          )
        }

        if (
          state.formData.stripe.depositPercentage
            .toString()
            .match(/^\d+(\.\d{3,})$/)
        ) {
          return new Error(t('calendar_advanced.forms_payment.stripe_error'))
        }
        return true
      },
    },
    chargeDescription: {
      trigger: ['input', 'blur'],
      validator() {
        if (!state.formData.stripe.chargeDescription) {
          return true
        }
        if (state.formData.stripe.chargeDescription.length > 1000) {
          return new Error(
            t('calendar_advanced.forms_payment.payment_description_error')
          )
        }
        return true
      },
    },
  }
})

const emit = defineEmits(['onSave', 'onCancel'])

const onClickTab = tab => {
  state.formData.stripe.depositType = tab
}

const methods = {
  async loadData() {
    state.shouldAcceptPayment = Boolean(
      editingCalendar.value?.stripe?.hasOwnProperty('amount')
    )
    state.shouldAcceptDepositAmount = Boolean(
      editingCalendar.value?.stripe?.hasOwnProperty('deposit')
    )
    state.formData = {
      isLivePaymentMode: Boolean(editingCalendar.value?.isLivePaymentMode),
      stripe: {
        amount: String(editingCalendar.value?.stripe?.amount || 0) || '0',
        currency: editingCalendar.value?.stripe?.currency || 'USD',
        chargeDescription:
          editingCalendar.value?.stripe?.chargeDescription || '',
        depositAmount:
          (editingCalendar.value?.stripe?.depositType === DepositType.AMOUNT &&
            editingCalendar.value?.stripe?.deposit) ||
          0,
        depositPercentage:
          (editingCalendar.value?.stripe?.depositType ===
            DepositType.PERCENTAGE &&
            editingCalendar.value?.stripe?.deposit) ||
          0,
        depositType:
          editingCalendar.value?.stripe?.depositType || DepositType.AMOUNT,
        recurringAppointmentsPaymentType:
          editingCalendar.value?.stripe?.recurringAppointmentsPaymentType ||
          RecurringAppointmentsPaymentType.FirstAppointmentOnly,
        isCouponEnabled:
          editingCalendar.value?.stripe?.isCouponEnabled || false,
      },
      enableChargeGuests: Boolean(editingCalendar.value?.enableChargeGuests),
    }
  },
  async emitSave() {
    if (state.shouldAcceptPayment) {
      try {
        await stripeFormRef.value.getForm().validate()
      } catch (error) {
        return
      }
    }

    const payload = {
      stripe: state.shouldAcceptPayment
        ? {
            amount: state.formData.stripe.amount,
            currency: state.formData.stripe.currency,
            chargeDescription: state.formData.stripe.chargeDescription,
            isCouponEnabled: state.formData.stripe.isCouponEnabled,
          }
        : {},
      isLivePaymentMode: state.formData.isLivePaymentMode,
      enableChargeGuests: state.shouldAcceptPayment
        ? state.formData.enableChargeGuests
        : false,
    } as any

    if (state.shouldAcceptPayment) {
      if (state.shouldAcceptDepositAmount) {
        payload.stripe = {
          ...payload.stripe,
          deposit:
            state.formData.stripe.depositType === DepositType.AMOUNT
              ? state.formData.stripe.depositAmount
              : state.formData.stripe.depositPercentage,
          depositType: state.formData.stripe.depositType,
        }
      }

      if (isRecurringPaymentEnabled.value) {
        payload.stripe = {
          ...payload.stripe,
          recurringAppointmentsPaymentType:
            state.formData.stripe.recurringAppointmentsPaymentType,
        }
      }
    }

    if (payload?.stripe?.amount) {
      payload.stripe.amount = Number(payload.stripe.amount)
    }

    return payload
  },
}

defineExpose({
  methods,
})

watch(
  () => state.shouldAcceptPayment,
  newValue => {
    AppState.shouldAcceptPayment = newValue
    if (!newValue) {
      state.formData.enableChargeGuests = false
    }
  }
)

watch(
  () => editingCalendar.value,
  () => {
    if (editingCalendar.value) {
      methods.loadData()
    }
  },
  { immediate: true }
)

watch(
  () => state.formData.stripe.amount,
  newValue => {
    if (newValue <= 0) {
      state.shouldAcceptDepositAmount = false
      state.formData.stripe.depositAmount = 0
    }
  }
)

watch(
  () => state.shouldAcceptDepositAmount,
  () => (AppState.isPartialPaymentChecked = state.shouldAcceptDepositAmount)
)

watch(
  () => AppState.editCalendarValidationData.recurringPaymentType,
  () => {
    state.formData.stripe.recurringAppointmentsPaymentType =
      AppState.editCalendarValidationData.recurringPaymentType
  }
)

watch(
  () => AppState.isPartialPaymentChecked,
  () => {
    if (!AppState.isPartialPaymentChecked) {
      state.shouldAcceptDepositAmount = false
    }
  }
)

watch(
  () => AppState.editCalendarValidationData.enableGuests,
  () => {
    if (!AppState.editCalendarValidationData.enableGuests) {
      state.formData.enableChargeGuests = false
    }
  }
)

watch(
  () => AppState.editCalendarValidationData.recurringPaymentType,
  () => {
    state.formData.stripe.recurringAppointmentsPaymentType =
      AppState.editCalendarValidationData.recurringPaymentType
  }
)

const isConnectedPaymentProviderSupported = computed(() => {
  return checkForValidPaymentProvider(AppState.paymentProviderInfo)
})

const isPaymentProviderConnected = computed(() => {
  return !!AppState.defaultPaymentProvider || AppState.isPayPalEnabled
})

const statusClasses = computed(() => {
  if (!isPaymentProviderConnected.value) {
    return 'bg-error-100 text-error-700'
  }
  return isConnectedPaymentProviderSupported.value
    ? 'bg-primary-50 text-primary-700'
    : 'bg-warning-50 text-warning-700'
})

const paymentProviderMessage = computed(() => {
  return isConnectedPaymentProviderSupported.value ||
    (AppState.isPayPalEnabled && AppState.defaultPaymentProvider === 'paypal')
    ? t('calendar_advanced.forms_payment.default')
    : isPaymentProviderConnected.value
    ? t('calendar_advanced.forms_payment.provider_not_supported')
    : t('calendar_advanced.forms_payment.no_default_payment_provider')
})

const isCustomPaymentProvider = computed(() => {
  return AppState.defaultPaymentProvider === PaymentProvider.CUSTOM_PROVIDER
})

const onClickConnectPaymentProvider = () => {
  AppState.parentApp?.then(parent => {
    parent.emit('go-to-payments-integrations-page', {
      providerId: undefined,
    })
  })
}

const maxAmount = computed(() => {
  return calculateMaxAmount(state.formData.stripe.currency)
})
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="payments-header"
      :header="$t('calendar_advanced.payments.payments')"
      :description="$t('calendar_advanced.payments.payments_description')"
    />
    <div class="my-[15px] border-t border-gray-200" />

    <div>
      <div class="flex w-full flex-row items-center justify-between">
        <div class="flex items-center space-x-3 pb-3">
          <div id="calendars-acceptPayments-switch">
            <UISwitch
              v-model:value="state.shouldAcceptPayment"
              :aria-label="t('aria_labels.should_accept_payment')"
              size="small"
              :disabled="!isPaymentProviderConnected"
            />
          </div>

          <div class="font-normal">
            {{ $t('calendar_advanced.forms_payment.accept_payments') }}
          </div>
          <UITooltip
            v-if="!isServiceCalendar"
            :title="
              isPaymentProviderConnected
                ? $t('calendar_advanced.forms_payment.accept_payments_tooltip')
                : $t(
                    'calendar_advanced.forms_payment.payment_disable_text_integration'
                  )
            "
            placement="top"
          >
            <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
          </UITooltip>
        </div>
      </div>

      <!-- Payment Mode -->
      <div v-if="state.shouldAcceptPayment">
        <div class="mt-3 flex flex-col items-start pb-2">
          <div class="flex flex-col items-start gap-3 pb-2">
            <div class="font-medium text-gray-700">
              {{ $t('calendar_advanced.forms_payment.payment_mode') }}
            </div>
            <div class="flex flex-row items-center gap-2">
              <div class="font-normal">
                {{ $t('calendar_advanced.forms_payment.test') }}
              </div>
              <div id="calendars-payment-live">
                <UISwitch
                  v-model:value="state.formData.isLivePaymentMode"
                  :aria-label="t('aria_labels.payment_live')"
                  size="small"
                />
              </div>
              <div class="font-normal">
                {{ $t('calendar_advanced.forms_payment.live') }}
              </div>
              <UITooltip
                :title="
                  $t('calendar_advanced.forms_payment.payment_mode_tooltip')
                "
                placement="top"
              >
                <InfoCircleIcon
                  class="h-5 w-5 cursor-pointer p-[2px] pb-0.5 text-gray-500"
                />
              </UITooltip>
            </div>
          </div>
        </div>

        <div
          v-if="!state.formData.isLivePaymentMode"
          class="flex items-center pb-3"
        >
          <div
            class="flex w-[450px] flex-row items-start gap-2 rounded-sm bg-warning-25 p-[10px]"
            id="test-mode-warning-alert"
          >
            <AlertTriangleIcon class="mt-[4px] h-4 w-4 text-warning-600" />
            <div class="w-[90%] text-sm font-normal leading-6 text-warning-700">
              {{ t('calendar_advanced.forms_payment.test_mode_warning') }}
            </div>
          </div>
        </div>
      </div>

      <div class="">
        <UIForm
          id="stripe-payment-form"
          ref="stripeFormRef"
          :model="state.formData"
          :rules="rules"
        >
          <UIFormItem
            v-if="state.shouldAcceptPayment"
            :label="$t('create_calendar.total_amount')"
            class="mt-4 w-full"
            path="stripeAmount"
            :show-feedback="state.isPaymentError"
            :tooltip="
              getTooltip(
                $t('calendar_advanced.forms_payment.total_amount_tooltip')
              )
            "
          >
            <UIInputGroup>
              <UIInputNumber
                id="calendars-stripe-amount"
                v-model="state.formData.stripe.amount"
                placeholder=""
                :min="0"
                :max="maxAmount"
                :show-button="false"
                size="large"
                class="w-full"
              />
              <UISelect
                id="calendars-stripe-currency"
                v-model:value="state.formData.stripe.currency"
                :filterable="true"
                class="w-32"
                :options="paymentCurrencyOptions"
              />
            </UIInputGroup>
          </UIFormItem>

          <div
            class="mt-[10px] flex flex-row items-center justify-between"
            :class="{
              'mb-[20px]': isRecurringPaymentEnabled,
              'pb-2': isPaypalWithRazorpay,
            }"
          >
            <div class="flex flex-row items-center !gap-2">
              <div
                v-if="isPaymentProviderConnected"
                :class="{
                  'w-[50px]': isCustomPaymentProvider,
                  'w-[20px]': !isCustomPaymentProvider,
                  'w-[30px]': AppState.defaultPaymentProvider === PaymentProvider.MERCADO_PAGO,
                }"
              >
                <img
                  :title="AppState.defaultPaymentProvider"
                  class="object-contain"
                  :src="getDefaulPaymentMethod(AppState.defaultPaymentProvider)"
                  :alt="t('alt_text.payment_provider')"
                />
              </div>
              <div
                class="w-fit rounded-2xl px-2 py-0.5 text-[13px] font-normal"
                :class="statusClasses"
              >
                {{ paymentProviderMessage }}
              </div>
            </div>

            <div class="flex cursor-pointer flex-row items-center gap-2">
              <div
                class="cursor-pointer text-[13px] font-medium text-primary-700"
                role="button"
                @click="onClickConnectPaymentProvider"
                @keydown="onClickConnectPaymentProvider"
                tabindex="0"
              >
                {{
                  $t('calendar_advanced.forms_payment.manage_payment_provider')
                }}
              </div>
              <GHLUITooltip>
                <template #trigger>
                  <InfoCircleIcon class="h-4 w-4 text-gray-500" />
                </template>
                <div :style="{ fontFamily: 'Inter' }">
                  {{
                    t('calendar_advanced.forms_payment.supported_providers')
                  }}:
                  <br />
                  <li
                    v-for="provider in getSupportedPaymentProviders()"
                    :key="provider"
                    class="list-none"
                  >
                    {{ provider }}
                  </li>
                </div>
              </GHLUITooltip>
            </div>
          </div>
          <div
            v-if="isPaypalWithRazorpay"
            class="mt-1"
            :class="{ 'mb-5': isRecurringPaymentEnabled }"
          >
            <UIAlert
              id="notification-alert"
              :closable="false"
              type="warning"
              class="bg-white text-sm"
            >
              <template #content>{{
                t('calendar_advanced.forms_payment.paypal_razorpay_helper_text')
              }}</template>
            </UIAlert>
          </div>

          <div
            v-if="
              state.shouldAcceptPayment &&
              !AppState.editCalendarValidationData.enableRecurring
            "
            class="mb-5 mt-6"
          >
            <div
              v-if="isPartialPaymentEnabled"
              class="mt-2 flex items-center space-x-3"
            >
              <UISwitch
                id="calendars-collect-deposit"
                v-model:value="state.shouldAcceptDepositAmount"
                size="small"
                :aria-label="t('aria_labels.collect_deposit')"
              />
              <div class="font-normal">
                {{ $t('create_calendar.collect_deposit_amount') }}
              </div>
              <UITooltip
                :title="
                  $t('calendar_advanced.forms_payment.partial_payment_tooltip')
                "
                placement="top"
              >
                <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
              </UITooltip>
            </div>

            <div
              v-else-if="state.formData.stripe.amount <= 0"
              class="flex items-center space-x-3"
            >
              <UITooltip
                placement="left"
                :title="
                  $t(
                    'calendar_advanced.forms_payment.partial_payment_enable_error'
                  )
                "
              >
                <div class="flex items-center space-x-3">
                  <UISwitch
                    id="calendars-collect-deposit"
                    v-model:value="state.shouldAcceptDepositAmount"
                    :disabled="true"
                    size="small"
                    :aria-label="t('aria_labels.collect_deposit')"
                  />

                  <div
                    class="font-normal"
                    :class="{ 'opacity-50': state.formData.stripe.amount <= 0 }"
                  >
                    {{ $t('create_calendar.collect_deposit_amount') }}
                  </div>
                </div>
              </UITooltip>
              <UITooltip
                :title="
                  $t('calendar_advanced.forms_payment.partial_payment_tooltip')
                "
                placement="top"
              >
                <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
              </UITooltip>
            </div>

            <div
              v-if="state.shouldAcceptDepositAmount && isPartialPaymentEnabled"
              class="relative mt-4 flex w-52"
            >
              <UITabs
                v-model="state.formData.stripe.depositType"
                :default-value="state.formData.stripe.depositType"
                :type="'segment'"
                :animated="true"
                @update:value="val => onClickTab(val)"
              >
                <UITabPane name="amount" :tab="$t('create_calendar.amount')">
                  <div class="hidden">{{ t('alt_text.test1') }}</div>
                </UITabPane>
                <UITabPane
                  name="percentage"
                  :tab="$t('create_calendar.percentage')"
                >
                  <div class="hidden">{{ t('alt_text.test2') }}</div>
                </UITabPane>
              </UITabs>
            </div>

            <div
              v-if="state.shouldAcceptDepositAmount && isPartialPaymentEnabled"
              class="mb-2 mt-2 flex w-[430px] flex-col"
            >
              <UIFormItem
                :path="
                  state.formData.stripe.depositType === DepositType.AMOUNT
                    ? 'depositAmount'
                    : 'depositPercentage'
                "
                :label="$t('create_calendar.deposit_amount')"
                :tooltip="
                  getTooltip(
                    $t('calendar_advanced.forms_payment.deposit_amount_tooltip')
                  )
                "
              >
                <UIInputGroup
                  v-if="
                    state.formData.stripe.depositType === DepositType.AMOUNT
                  "
                >
                  <UIInputNumber
                    id="calendars-deposit-stripe-amount"
                    v-model="state.formData.stripe.depositAmount"
                    placeholder=""
                    :min="0"
                    :max="maxAmount"
                    :show-button="false"
                    size="large"
                    class="w-full"
                  />
                  <UIInputGroupLabel class="w-20 text-center">{{
                    state.formData.stripe.currency
                  }}</UIInputGroupLabel>
                </UIInputGroup>

                <UIInputGroup v-else>
                  <UIInputNumber
                    id="calendars-deposit-stripe-amount"
                    v-model="state.formData.stripe.depositPercentage"
                    placeholder=""
                    :min="0"
                    :max="maxAmount"
                    :show-button="false"
                    size="large"
                    class="w-full"
                  />
                  <UIInputGroupLabel class="w-20 text-center">
                    <Percent02Icon class="mb-1 h-5 w-5" />
                  </UIInputGroupLabel>
                </UIInputGroup>
              </UIFormItem>
            </div>
          </div>

          <div
            v-if="
              AppState.editCalendarValidationData.enableGuests &&
              state.shouldAcceptPayment
            "
            class="mb-5 flex items-center space-x-3"
            :class="{ '-mt-6': state.shouldAcceptDepositAmount }"
          >
            <UISwitch
              v-model:value="state.formData.enableChargeGuests"
              size="small"
              :aria-label="t('aria_labels.enable_charge_guests')"
            />
            <div class="font-normal">
              {{
                $t(
                  'calendar_advanced.forms_payment.multiply_amount_guests_all_guests'
                )
              }}
            </div>

            <Tooltip
              :message="
                $t(
                  'calendar_advanced.forms_payment.multiply_amount_guests_all_guests_tooltip'
                )
              "
            />
          </div>

          <div
            v-if="state.shouldAcceptPayment && AppState.isCouponEnabled"
            class="mb-5 flex items-center space-x-3"
          >
            <UISwitch
              v-model:value="state.formData.stripe.isCouponEnabled"
              size="small"
              :aria-label="t('aria_labels.enable_coupon')"
            />
            <div class="font-normal">
              {{ $t('calendar_advanced.forms_payment.enable_coupon') }}
            </div>
            <Tooltip
              :message="
                $t('calendar_advanced.forms_payment.enable_coupon_tooltip')
              "
            />
          </div>

          <UIFormItem
            v-if="state.shouldAcceptPayment && isRecurringPaymentEnabled"
            :label="
              $t('calendar_advanced.forms_payment.recurring_payment_label')
            "
            path="chargeDescription"
          >
            <UISelect
              id="calendar-select-recurring-appointment-payment"
              v-model:value="
                state.formData.stripe.recurringAppointmentsPaymentType
              "
              class="w-96"
              :options="recurringAppointmentPaymentOptions(t)"
            />
          </UIFormItem>

          <UIFormItem
            v-if="state.shouldAcceptPayment"
            :label="$t('calendar_advanced.forms_payment.payment_description')"
            path="chargeDescription"
            :tooltip="
              getTooltip(
                $t('calendar_advanced.forms_payment.payment_description_desc')
              )
            "
          >
            <UIInput
              id="calendar-stripe-currency-input"
              v-model="state.formData.stripe.chargeDescription"
              type="textarea"
              :placeholder="
                $t(
                  'calendar_advanced.forms_payment.payment_description_tooltip'
                )
              "
              :rows="4"
              :show-count="true"
              :maxlength="1000"
            />
          </UIFormItem>
        </UIForm>
      </div>
    </div>
  </div>
</template>
